"use client";

import { useState, useCallback } from "react";

const STORAGE_PREFIX = "data-table-column-visibility:";

function loadHidden(
  storageKey: string | null,
  defaultHidden?: readonly string[],
): Set<string> {
  const defaults = () => new Set(defaultHidden ?? []);
  if (typeof window === "undefined" || !storageKey) return defaults();
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
      return new Set(parsed as string[]);
    }
    return defaults();
  } catch {
    return defaults();
  }
}

/**
 * @param defaultHidden column ids hidden until the user makes a choice. Once
 * the user touches the toggle, their explicit state is persisted — including
 * "everything visible" (stored as `[]`, NOT removed, so defaults don't
 * reapply on the next visit).
 */
export function useColumnVisibility(
  key: string | undefined,
  defaultHidden?: readonly string[],
) {
  const storageKey = key ? `${STORAGE_PREFIX}${key}` : null;
  const hasDefaults = !!defaultHidden && defaultHidden.length > 0;

  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(
    () => loadHidden(storageKey, defaultHidden),
  );

  const toggleColumn = useCallback(
    (columnId: string) => {
      setHiddenColumns((prev) => {
        const next = new Set(prev);
        if (next.has(columnId)) {
          next.delete(columnId);
        } else {
          next.add(columnId);
        }
        if (storageKey) {
          try {
            if (next.size === 0 && !hasDefaults) {
              // No defaults → absent key already means "all visible".
              localStorage.removeItem(storageKey);
            } else {
              // With defaults, even an empty set must be stored explicitly,
              // otherwise the defaults would re-hide columns on next load.
              localStorage.setItem(storageKey, JSON.stringify([...next]));
            }
          } catch {
            // ignore
          }
        }
        return next;
      });
    },
    [storageKey, hasDefaults],
  );

  return { hiddenColumns, toggleColumn };
}
